import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraLogo, SpecraProgramSidebar, SpecraWordmark } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Boxes, FileText, FolderOpen, LayoutDashboard, Library, Link2, Lock, MessageSquare, Shield, TrendingUp, Wrench } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useWorkflowAreas } from '../lib/access/useWorkflowAreas';
import { renderRouterLink } from '../lib/renderRouterLink';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { SidebarUserMenu } from './SidebarUserMenu';
const isDev = process.env.NODE_ENV === 'development';
// Roles that see the Admin console entry. Mirrors RequireAdminRoute /
// enies-lobby AdminRoles (case-normalized) — unchanged from GlobalNavItems.
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
const iconClass = 'w-4 h-4 flex-shrink-0';
const NO_AREAS_ITEM = {
    id: 'no-areas',
    label: 'No areas yet — ask an admin',
    icon: /*#__PURE__*/ _jsx(Lock, {
        className: iconClass
    }),
    testId: Testids.appNav.noAreas
};
/**
 * Route prefixes that light the Growth entry (GRW-01). Everyone: all of
 * /growth (hub, Growth Home, workspace lists), pursuits, opportunities,
 * landscapes and RFI responses. Growth-only users: the same minus /growth/home,
 * which lights their Dashboard ("Growth Home") item instead, so exactly one
 * item carries aria-current.
 */ export function growthMatchPaths(growthOnly) {
    return growthOnly ? [
        '/growth/rfi-responses',
        '/growth/proposals',
        '/capture',
        '/opportunities',
        '/landscapes',
        '/rfi-response'
    ] : [
        '/growth',
        '/capture',
        '/opportunities',
        '/landscapes',
        '/rfi-response'
    ];
}
/** The Workflows entries; each shows only where useWorkflowAreas finds its area reachable. */ export const WORKFLOW_ENTRIES = [
    {
        id: 'growth',
        label: 'Growth',
        href: '/growth',
        icon: /*#__PURE__*/ _jsx(TrendingUp, {
            className: iconClass
        }),
        // Growth Home belongs to Growth. For growth-only users the Dashboard item
        // already is Growth Home, so it is excluded here to keep one active item
        // (growthMatchPaths below).
        matchPaths: growthMatchPaths(false),
        testId: Testids.growth.navItem,
        area: [
            'capture',
            'opportunities'
        ]
    },
    {
        id: 'delivery',
        label: 'Delivery',
        href: '/delivery',
        icon: /*#__PURE__*/ _jsx(Boxes, {
            className: iconClass
        }),
        testId: Testids.delivery.navItem,
        area: 'delivery'
    }
];
/**
 * Application-level left navigation (outside a program). Mirrors the redesign
 * mockup's app taxonomy — Dashboard, then Workflows / Tools / Admin groups —
 * reconciled to the routes that actually exist (mockup stubs like Organization
 * / Integrations are omitted; Opportunities and SharePoint are real routes the
 * mockup lacks, placed in the nearest group). Renders through the shared
 * SpecraProgramSidebar shell with a logo brand instead of a program identity.
 */ export function AppSidebar() {
    const location = useLocation();
    const { user } = useAuth();
    // Collapse lives in the layout store so a focus surface (the in-flow
    // document editor) can fold the rail and hand its width to the editor.
    const desktopCollapsed = useLayoutStore((state)=>state.sidebarCollapsed);
    const isNarrow = useMediaQuery('(max-width: 767px)');
    const collapsed = isNarrow || desktopCollapsed;
    const toggleCollapsed = useLayoutStore((state)=>state.toggleSidebarCollapsed);
    const [groupState, setGroupState] = useState({});
    const isAdmin = !!user?.role && ADMIN_ROLES.has(user.role.toLowerCase());
    const { isLoading: areasLoading, reachable, growthOnly } = useWorkflowAreas();
    // ⌘\ / Ctrl+\ toggles the rail (LAYOUT-SPEC §7), matching the program rail.
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === '\\' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleCollapsed();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        toggleCollapsed
    ]);
    const primary = useMemo(()=>[
            {
                id: 'dashboard',
                label: growthOnly ? 'Growth Home' : 'Dashboard',
                href: growthOnly ? '/growth/home' : '/dashboard',
                icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
                    className: iconClass
                })
            }
        ], [
        growthOnly
    ]);
    const workflows = useMemo(()=>WORKFLOW_ENTRIES.filter((def)=>[
                def.area
            ].flat().some((area)=>reachable[area])).map((def)=>def.id === 'growth' ? {
                ...def,
                matchPaths: growthMatchPaths(growthOnly)
            } : def), [
        reachable,
        growthOnly
    ]);
    const groupDefs = useMemo(()=>[
            // Held while access resolves: rendering the links first and removing
            // the unreachable ones after would flash dead links.
            ...areasLoading ? [] : [
                {
                    id: 'workflows',
                    label: 'Workflows',
                    items: workflows
                }
            ],
            // Registries sit at the root, not under Delivery — they're tenant-wide
            // assets (companies, contacts, capabilities, vehicles, role types)
            // consumed by capture and proposal too. Labeled "Company Registry" so it
            // collapses like the other groups; Projects stays first.
            {
                id: 'registries',
                label: 'Company Registry',
                items: [
                    {
                        id: 'projects',
                        label: 'Projects',
                        href: '/projects',
                        icon: /*#__PURE__*/ _jsx(FolderOpen, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'registries',
                        label: 'Registries',
                        href: '/registries',
                        icon: /*#__PURE__*/ _jsx(Library, {
                            className: iconClass
                        }),
                        testId: Testids.registries.navItem
                    }
                ]
            },
            {
                id: 'tools',
                label: 'Tools',
                items: [
                    {
                        id: 'chat',
                        label: 'Chat',
                        href: '/assistant',
                        icon: /*#__PURE__*/ _jsx(MessageSquare, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'documents',
                        label: 'Documents',
                        href: '/documents',
                        icon: /*#__PURE__*/ _jsx(FileText, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'sharepoint',
                        label: 'SharePoint',
                        href: '/sharepoint-documents',
                        icon: /*#__PURE__*/ _jsx(Link2, {
                            className: iconClass
                        })
                    },
                    // D12: every member can delegate their own access, not just
                    // grantors/admins — AssignmentsListPage itself picks the
                    // self-service vs. power-tool view server-side, so this entry is
                    // never gated on ADMIN_ROLES.
                    {
                        id: 'delegate-access',
                        label: 'Delegate my access',
                        href: '/admin/access',
                        icon: /*#__PURE__*/ _jsx(Shield, {
                            className: iconClass
                        }),
                        testId: Testids.adminAccess.navItem
                    }
                ]
            }
        ], [
        areasLoading,
        workflows
    ]);
    const isActive = useCallback((def)=>{
        const { pathname } = location;
        const under = (path)=>pathname === path || pathname.startsWith(`${path}/`);
        return def.matchPaths ? pathname === def.href || def.matchPaths.some(under) : under(def.href);
    }, [
        location
    ]);
    const toItem = useCallback((def)=>({
            id: def.id,
            label: def.label,
            icon: def.icon,
            href: def.href,
            active: isActive(def),
            testId: def.testId
        }), [
        isActive
    ]);
    const groups = useMemo(()=>{
        const adminGroup = isAdmin ? [
            {
                id: 'admin',
                label: 'Admin',
                href: '/admin',
                icon: /*#__PURE__*/ _jsx(Shield, {
                    className: iconClass
                }),
                testId: Testids.adminConsole.nav
            }
        ] : [];
        const devGroup = isDev ? [
            {
                id: 'dev-tools',
                label: 'Tools Library (Dev)',
                href: '/dev/tools',
                icon: /*#__PURE__*/ _jsx(Wrench, {
                    className: iconClass
                })
            }
        ] : [];
        return [
            {
                id: 'primary',
                items: primary.map(toItem)
            },
            ...groupDefs.map((group)=>({
                    id: group.id,
                    label: group.label,
                    items: group.id === 'workflows' && group.items.length === 0 ? [
                        NO_AREAS_ITEM
                    ] : group.items.map(toItem)
                })),
            ...adminGroup.length ? [
                {
                    id: 'admin',
                    label: 'Admin',
                    items: adminGroup.map(toItem)
                }
            ] : [],
            ...devGroup.length ? [
                {
                    id: 'dev',
                    items: devGroup.map(toItem)
                }
            ] : []
        ];
    }, [
        groupDefs,
        isAdmin,
        primary,
        toItem
    ]);
    return /*#__PURE__*/ _jsx(SpecraProgramSidebar, {
        brand: /*#__PURE__*/ _jsx(Link, {
            className: "hover:opacity-80 transition-opacity",
            title: "Home",
            to: growthOnly ? '/growth/home' : '/dashboard',
            children: collapsed ? /*#__PURE__*/ _jsx(SpecraLogo, {
                variant: "mark",
                size: 22
            }) : /*#__PURE__*/ _jsx(SpecraWordmark, {
                height: 16
            })
        }),
        className: "h-full max-md:max-h-none max-md:w-[var(--component-sidebar-collapsed-width)]",
        collapsed: collapsed,
        collapseToggleTestId: Testids.appNav.collapse,
        "data-testid": Testids.appNav.sidebar,
        footer: user ? /*#__PURE__*/ _jsx(SidebarUserMenu, {
            collapsed: collapsed,
            user: user
        }) : undefined,
        getGroupProps: (group)=>{
            if (!group.label) return {};
            const containsActive = group.items.some((item)=>item.active);
            // No explicit choice yet: only Workflows and the group holding the
            // current page start open. Once toggled, the user's choice wins.
            const open = groupState[group.id] ?? (group.id === 'workflows' || containsActive);
            return {
                collapsible: true,
                containsActive,
                labelTestId: Testids.appNav.groupLabel,
                onToggle: ()=>setGroupState((state)=>({
                            ...state,
                            [group.id]: !open
                        })),
                open
            };
        },
        groups: groups,
        navigationLabel: "Main navigation",
        onCollapseToggle: isNarrow ? undefined : toggleCollapsed,
        renderLink: renderRouterLink
    });
}
